import { useLocation, useNavigate } from 'react-router-dom';
import { trackNavLinkClick } from '../analytics/analytics';

export const calculators = [
  {
    id: 'runway',
    group: 'Financial planning',
    label: 'Financial Runway',
    description: 'See how long your savings could last.',
    icon: 'R',
    event: 'nav_tool_runway',
  },
  {
    id: 'emi',
    group: 'Loans & debt',
    label: 'EMI Calculator',
    description: 'Estimate monthly loan payments and interest.',
    icon: 'E',
    event: 'nav_tool_emi',
  },
  {
    id: 'sip',
    group: 'Investing',
    label: 'SIP Calculator',
    description: 'Project the growth of monthly investments.',
    icon: 'S',
    event: 'nav_tool_sip',
  },
  {
    id: 'lumpsum',
    group: 'Investing',
    label: 'Lump-sum Calculator',
    description: 'Project a one-time investment over time.',
    icon: 'L',
    event: 'nav_tool_lumpsum',
  },
  {
    id: 'fd',
    group: 'Savings & deposits',
    label: 'FD Calculator',
    description: 'Estimate fixed-deposit maturity and interest.',
    icon: 'F',
    event: 'nav_tool_fd',
  },
  {
    id: 'rd',
    group: 'Savings & deposits',
    label: 'RD Calculator',
    description: 'Estimate recurring-deposit maturity and interest.',
    icon: 'D',
    event: 'nav_tool_rd',
  },
] as const;

export type CalculatorId = typeof calculators[number]['id'];
export type WorkspaceViewId = CalculatorId | 'net-worth';

interface CalculatorSidebarProps {
  activeCalculator: WorkspaceViewId;
  onSelectCalculator: (calculator: WorkspaceViewId) => void;
}

export function CalculatorSidebar({ activeCalculator, onSelectCalculator }: CalculatorSidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <aside className="calculator-sidebar">
      <div className="calculator-sidebar-heading">
        <span className="calculator-sidebar-title">Financial tools</span>
      </div>
      <nav aria-label="Calculator navigation">
        <ul className="calculator-sidebar-list">
          {['Financial planning', 'Loans & debt', 'Investing', 'Savings & deposits'].map((group) => (
            <li className="calculator-sidebar-group" key={group}>
              <span className="calculator-sidebar-group-title">{group}</span>
              <ul>
                {group === 'Financial planning' && (
                  <li>
                    <button
                      type="button"
                      className={`calculator-sidebar-link${pathname === '/' && activeCalculator === 'net-worth' ? ' active' : ''}`}
                      aria-current={pathname === '/' && activeCalculator === 'net-worth' ? 'page' : undefined}
                      onClick={() => {
                        onSelectCalculator('net-worth');
                        if (pathname !== '/') navigate('/');
                        trackNavLinkClick('nav_net_worth_sidebar');
                      }}
                    >
                      <span className="calculator-sidebar-icon" aria-hidden="true">N</span>
                      <span className="calculator-sidebar-label">Net Worth</span>
                    </button>
                  </li>
                )}
                {calculators.filter((calculator) => calculator.group === group).map((calculator) => {
                  const isActive = pathname === '/' && activeCalculator === calculator.id;
                  return (
                    <li key={calculator.id}>
                      <button
                        type="button"
                        className={`calculator-sidebar-link${isActive ? ' active' : ''}`}
                        aria-current={isActive ? 'page' : undefined}
                        title={calculator.label}
                        onClick={() => {
                          onSelectCalculator(calculator.id);
                          if (pathname !== '/') navigate('/');
                          trackNavLinkClick(`${calculator.event}_sidebar`);
                        }}
                      >
                        <span className="calculator-sidebar-icon" aria-hidden="true">{calculator.icon}</span>
                        <span className="calculator-sidebar-label">{calculator.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

interface CalculatorTilesProps {
  onSelectCalculator: (calculator: CalculatorId) => void;
}

export function CalculatorTiles({ onSelectCalculator }: CalculatorTilesProps) {
  return (
    <section className="calculator-tiles-section" aria-labelledby="calculator-tiles-title">
      <div className="calculator-tiles-heading">
        <div>
          <span className="emi-eyebrow">FINANCIAL TOOLS</span>
          <h2 id="calculator-tiles-title">Choose a calculator</h2>
        </div>
        <p>Explore your finances with a calculator that fits your goal.</p>
      </div>
      <div className="calculator-tiles">
        {calculators.map((calculator) => (
          <button
            key={calculator.id}
            type="button"
            className="calculator-tile"
            onClick={() => {
              onSelectCalculator(calculator.id);
              trackNavLinkClick(`${calculator.event}_tile`);
            }}
          >
            <span className="calculator-tile-icon" aria-hidden="true">{calculator.icon}</span>
            <span className="calculator-tile-title">{calculator.label}</span>
            <span className="calculator-tile-description">{calculator.description}</span>
            <span className="calculator-tile-action">Open calculator <span aria-hidden="true">→</span></span>
          </button>
        ))}
      </div>
    </section>
  );
}
